import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const GameSection = styled.div`
  text-align: center;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  margin: 30px auto;
  max-width: 1000px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const GameTitle = styled.h2`
  font-size: 2.2em;
  color: #fff;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  margin-bottom: 10px;
`;

const GameInstructions = styled.p`
  font-size: 1.1em;
  color: #ffe066;
  margin-bottom: 20px;
`;

const GameBtn = styled.button`
  padding: 15px 40px;
  font-size: 1.3em;
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: white;
  border: none;
  border-radius: 30px;
  cursor: pointer;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s;
  font-family: 'Comic Sans MS', cursive, sans-serif;
  font-weight: bold;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 7px 20px rgba(0, 0, 0, 0.4);
  }
`;

const GameStats = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 15px;
  margin: 20px 0;
  padding: 15px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  color: white;
  font-size: 1.2em;
  font-weight: bold;
`;

const GameCanvas = styled.canvas`
  width: 100%;
  max-width: 800px;
  height: 500px;
  background: linear-gradient(to bottom, #87ceeb, #98d8e8);
  border: 5px solid white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  display: block;
  margin: 0 auto;

  @media (max-width: 768px) {
    height: 450px;
  }

  @media (max-width: 480px) {
    height: 380px;
  }
`;

const GameOver = styled.div`
  padding: 30px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 20px;
  color: white;

  h2 {
    font-size: 2.5em;
    margin-bottom: 15px;
    color: #ff6b6b;
  }

  p {
    font-size: 1.3em;
    margin: 10px 0;
  }
`;

const GameWon = styled.div`
  padding: 30px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 20px;
  color: white;

  h2 {
    font-size: 2.5em;
    margin-bottom: 15px;
    color: #ffd700;
  }

  p {
    font-size: 1.3em;
    margin: 10px 0;
  }
`;

const LevelComplete = styled.div`
  padding: 40px;
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.95), rgba(255, 165, 0, 0.95));
  border-radius: 20px;
  color: #333;
  box-shadow: 0 10px 40px rgba(255, 215, 0, 0.5);
  border: 3px solid #ffd700;

  h2 {
    font-size: 2.8em;
    margin-bottom: 15px;
    color: #8b4513;
    text-shadow: 2px 2px 4px rgba(255, 255, 255, 0.5);
  }

  p {
    font-size: 1.4em;
    margin: 10px 0;
    color: #5d4037;
    font-weight: bold;
  }

  .level-name {
    font-size: 1.6em;
    color: #d32f2f;
    margin: 15px 0;
    font-style: italic;
  }
`;

function Game() {
  const canvasRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [levelComplete, setLevelComplete] = useState(false);
  const [completedLevel, setCompletedLevel] = useState(null);
  const [stats, setStats] = useState({
    level: 1,
    score: 0,
    lives: 3,
    time: 60
  });

  const gameStateRef = useRef({
    cat: { x: 0, y: 0, width: 80, height: 60, speed: 8 },
    catEmoji: '😺',
    catEmojiTimer: null,
    items: [],
    enemies: [],
    keys: {},
    animationId: null,
    lastSpawn: 0,
    gameStartTime: 0,
    particles: [],
    flashAlpha: 0
  });

  const levels = [
    { name: 'Journalism Intern (Needs Coffee)', spawnRate: 800, enemyChance: 0.15, timeLimit: 50, targetScore: 10, catEmoji: '😺', dogEmoji: '🐕' },
    { name: 'Weekend Wine Blogger', spawnRate: 700, enemyChance: 0.2, timeLimit: 45, targetScore: 20, catEmoji: '😸', dogEmoji: '🐶' },
    { name: 'Cat Dad with Pinot', spawnRate: 600, enemyChance: 0.25, timeLimit: 40, targetScore: 30, catEmoji: '😻', dogEmoji: '🐺' },
    { name: 'Investigative Wino Reporter', spawnRate: 550, enemyChance: 0.3, timeLimit: 35, targetScore: 40, catEmoji: '😼', dogEmoji: '🦊' },
    { name: 'Steven-Level Multitasker', spawnRate: 500, enemyChance: 0.35, timeLimit: 30, targetScore: 50, catEmoji: '😎', dogEmoji: '🐕‍🦺' },
    { name: 'Wine Cellar Correspondent', spawnRate: 450, enemyChance: 0.4, timeLimit: 28, targetScore: 65, catEmoji: '🦁', dogEmoji: '🦮' },
    { name: 'Feline Fact-Checker Extraordinaire', spawnRate: 400, enemyChance: 0.45, timeLimit: 25, targetScore: 80, catEmoji: '🐯', dogEmoji: '🐕‍🦺' },
    { name: 'Master of Truth & Tannins', spawnRate: 350, enemyChance: 0.5, timeLimit: 22, targetScore: 100, catEmoji: '🦁', dogEmoji: '🦴' },
    { name: 'Legendary Cat-Whispering Journalist', spawnRate: 300, enemyChance: 0.55, timeLimit: 20, targetScore: 125, catEmoji: '🐆', dogEmoji: '👹' },
    { name: 'STEVEN FROLKE HIMSELF! 🏆', spawnRate: 250, enemyChance: 0.6, timeLimit: 18, targetScore: 150, catEmoji: '👑', dogEmoji: '💀' }
  ];

  useEffect(() => {
    if (!isActive) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const state = gameStateRef.current;

    // Initialize game
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    state.cat.x = canvas.width / 2 - state.cat.width / 2;
    state.cat.y = canvas.height - state.cat.height - 10;
    state.gameStartTime = Date.now();
    state.particles = [];
    state.flashAlpha = 0;
    state.lastTimeUpdate = 0;
    
    // Set cat emoji based on current level
    const currentLevelData = levels[stats.level - 1];
    state.catEmoji = currentLevelData.catEmoji;

    // Keyboard controls
    const handleKeyDown = (e) => {
      if (['ArrowLeft', 'ArrowRight', 'a', 'd', 'A', 'D'].includes(e.key)) {
        e.preventDefault();
        state.keys[e.key] = true;
      }
    };

    const handleKeyUp = (e) => {
      state.keys[e.key] = false;
    };

    // Mouse controls (drag to move)
    let isDragging = false;

    const handleMouseDown = (e) => {
      isDragging = true;
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      
      // Directly set cat position to mouse location (centered on cat)
      state.cat.x = mouseX - state.cat.width / 2;
      state.cat.x = Math.max(0, Math.min(canvas.width - state.cat.width, state.cat.x));
    };

    const handleMouseUp = () => {
      isDragging = false;
    };

    // Touch controls for mobile
    let touchStartX = null;

    const handleTouchStart = (e) => {
      const rect = canvas.getBoundingClientRect();
      touchStartX = e.touches[0].clientX - rect.left;
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (touchStartX === null) return;
      
      const rect = canvas.getBoundingClientRect();
      const touchX = e.touches[0].clientX - rect.left;
      
      // Directly set cat position to touch location (centered on cat)
      state.cat.x = touchX - state.cat.width / 2;
      state.cat.x = Math.max(0, Math.min(canvas.width - state.cat.width, state.cat.x));
    };

    const handleTouchEnd = () => {
      touchStartX = null;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd);

    // Game loop
    const gameLoop = () => {
      const now = Date.now();
      const currentLevel = levels[stats.level - 1];

      // Update timer only when it changes (not every frame)
      const elapsedSeconds = Math.floor((now - state.gameStartTime) / 1000);
      const timeLeft = Math.max(0, currentLevel.timeLimit - elapsedSeconds);
      
      if (timeLeft !== state.lastTimeUpdate) {
        state.lastTimeUpdate = timeLeft;
        setStats(prev => ({ ...prev, time: timeLeft }));
      }

      if (timeLeft === 0) {
        endGame(false);
        return;
      }

      // Move cat
      if ((state.keys['ArrowLeft'] || state.keys['a'] || state.keys['A']) && state.cat.x > 0) {
        state.cat.x -= state.cat.speed;
      }
      if ((state.keys['ArrowRight'] || state.keys['d'] || state.keys['D']) && state.cat.x < canvas.width - state.cat.width) {
        state.cat.x += state.cat.speed;
      }

      // Spawn items
      if (now - state.lastSpawn > currentLevel.spawnRate) {
        state.lastSpawn = now;
        const isEnemy = Math.random() < currentLevel.enemyChance;
        const newItem = {
          x: Math.random() * (canvas.width - 40),
          y: -40,
          width: 40,
          height: 40,
          speed: 3 + stats.level * 0.4,
          isEnemy: isEnemy,
          emoji: isEnemy ? currentLevel.dogEmoji : ['🍷', '🍾', '🥂'][Math.floor(Math.random() * 3)]
        };
        
        if (isEnemy) {
          state.enemies.push(newItem);
        } else {
          state.items.push(newItem);
        }
      }

      // Update and draw items
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      [...state.items, ...state.enemies].forEach(item => {
        item.y += item.speed;
        ctx.font = '40px serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(item.emoji, item.x + item.width / 2, item.y + item.height / 2);

        // Collision detection
        if (
          item.y + item.height > state.cat.y &&
          item.y < state.cat.y + state.cat.height &&
          item.x + item.width > state.cat.x &&
          item.x < state.cat.x + state.cat.width
        ) {
          if (item.isEnemy) {
            // Show sad cat
            state.catEmoji = '😿';
            if (state.catEmojiTimer) clearTimeout(state.catEmojiTimer);
            state.catEmojiTimer = setTimeout(() => {
              state.catEmoji = currentLevel.catEmoji;
            }, 1000);
            
            setStats(prev => {
              const newLives = prev.lives - 1;
              if (newLives <= 0) {
                endGame(false);
              }
              return { ...prev, lives: newLives };
            });
            state.enemies = state.enemies.filter(e => e !== item);
          } else {
            // SPECTACULAR WINE CATCH!
            state.catEmoji = '😸';
            if (state.catEmojiTimer) clearTimeout(state.catEmojiTimer);
            state.catEmojiTimer = setTimeout(() => {
              state.catEmoji = currentLevel.catEmoji;
            }, 1000);
            
            // Create particle explosion (minimal for performance)
            const particleEmojis = ['✨', '💫', '🍷'];
            for (let i = 0; i < 4; i++) {
              const angle = (Math.PI * 2 * i) / 4;
              const speed = 4;
              state.particles.push({
                x: state.cat.x + state.cat.width / 2,
                y: state.cat.y + state.cat.height / 2,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                emoji: particleEmojis[Math.floor(Math.random() * particleEmojis.length)],
                life: 20,
                size: 25
              });
            }
            
            setStats(prev => {
              const newScore = prev.score + 1;
              if (newScore >= currentLevel.targetScore) {
                levelUp();
              }
              return { ...prev, score: newScore };
            });
            state.items = state.items.filter(i => i !== item);
          }
        }
      });

      // Remove off-screen items
      state.items = state.items.filter(item => item.y < canvas.height);
      state.enemies = state.enemies.filter(item => item.y < canvas.height);

      // Update and draw particles (optimized - minimal iterations)
      if (state.particles && state.particles.length > 0) {
        for (let i = state.particles.length - 1; i >= 0; i--) {
          const particle = state.particles[i];
          particle.x += particle.vx;
          particle.y += particle.vy;
          particle.vy += 0.3; // gravity
          particle.life--;
          
          if (particle.life > 0) {
            ctx.globalAlpha = particle.life / 20;
            ctx.font = `${particle.size}px serif`;
            ctx.textAlign = 'center';
            ctx.fillText(particle.emoji, particle.x, particle.y);
            ctx.globalAlpha = 1;
          } else {
            state.particles.splice(i, 1);
          }
        }
      }

      // Draw cat
      ctx.font = '60px serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(state.catEmoji, state.cat.x + state.cat.width / 2, state.cat.y + state.cat.height / 2);

      state.animationId = requestAnimationFrame(gameLoop);
    };

    gameLoop();

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      if (state.animationId) {
        cancelAnimationFrame(state.animationId);
      }
    };
  }, [isActive]);

  const startGame = () => {
    setIsActive(true);
    setGameOver(false);
    setGameWon(false);
    setStats({ level: 1, score: 0, lives: 3, time: 50 });
    gameStateRef.current = {
      cat: { x: 0, y: 0, width: 80, height: 60, speed: 8 },
      catEmoji: '😺',
      catEmojiTimer: null,
      items: [],
      enemies: [],
      keys: {},
      animationId: null,
      lastSpawn: 0,
      gameStartTime: 0
    };
  };

  const endGame = (won) => {
    setIsActive(false);
    if (won) {
      setGameWon(true);
    } else {
      setGameOver(true);
    }
    if (gameStateRef.current.animationId) {
      cancelAnimationFrame(gameStateRef.current.animationId);
    }
  };

  const levelUp = () => {
    if (stats.level >= 10) {
      endGame(true);
      return;
    }

    // Pause game and show level complete message
    setIsActive(false);
    setLevelComplete(true);
    setCompletedLevel(stats.level);
    
    if (gameStateRef.current.animationId) {
      cancelAnimationFrame(gameStateRef.current.animationId);
    }
  };

  const continueToNextLevel = () => {
    setLevelComplete(false);
    
    setStats(prev => ({
      level: prev.level + 1,
      score: 0,
      lives: 3,
      time: levels[prev.level].timeLimit
    }));
    
    gameStateRef.current.gameStartTime = Date.now();
    gameStateRef.current.items = [];
    gameStateRef.current.enemies = [];
    
    setIsActive(true);
  };

  return (
    <GameSection>
      <GameTitle>🎮 STEVEN FROLKE'S LEGENDARY WINE & CAT ADVENTURE 🎮</GameTitle>
      <GameInstructions>
        Help Steven catch fine wine 🍷 while avoiding pesky dogs 🐕! (Cats only!) Arrow Keys / A/D or Touch & Drag!
      </GameInstructions>
      
      {!isActive && !gameOver && !gameWon && !levelComplete && (
        <GameBtn onClick={startGame}>BEGIN THE FROLKE QUEST</GameBtn>
      )}
      
      {levelComplete && completedLevel && (
        <LevelComplete>
          <h2>🎉 LEVEL {completedLevel} COMPLETE! 🎉</h2>
          <p>🍷 Brilliant wine-catching journalism! 🍷</p>
          <p className="level-name">You mastered: {levels[completedLevel - 1].name}</p>
          <p>🐱 Steven's cats are impressed! Next challenge awaits!</p>
          <p>⭐ Get ready for: {levels[completedLevel].name}</p>
          <GameBtn onClick={continueToNextLevel}>CONTINUE THE FROLKE LEGACY</GameBtn>
        </LevelComplete>
      )}
      
      {isActive && (
        <>
          <GameStats>
            <div>Level: {stats.level} - {levels[stats.level - 1].name}</div>
            <div>Score: {stats.score}/{levels[stats.level - 1].targetScore}</div>
            <div>Lives: {'❤️'.repeat(stats.lives)}</div>
            <div>Time: {stats.time}s</div>
          </GameStats>
          <GameCanvas ref={canvasRef} />
        </>
      )}
      
      {gameOver && (
        <GameOver>
          <h2>🐕 TOO MANY DOGS! 🐕</h2>
          <p>Steven would never tolerate this!</p>
          <p>You reached Level {stats.level}: {levels[stats.level - 1].name}</p>
          <p>Wine Bottles Caught: {stats.score}</p>
          <p>"Every great journalist knows when to try again" - Steven Frolke (probably)</p>
          <GameBtn onClick={startGame}>RETRY THE FROLKE WAY</GameBtn>
        </GameOver>
      )}
      
      {gameWon && (
        <GameWon>
          <h2>🏆 LEGENDARY STATUS ACHIEVED! 🏆</h2>
          <p>You've reached Steven Frolke's level of excellence!</p>
          <p>You mastered all 10 levels of wine-catching journalism!</p>
          <p>🐱 Steven's cats approve! 🍷 The wine cellar is full! 📰 The stories write themselves!</p>
          <p>"This is what peak performance looks like" - Steven Frolke</p>
          <GameBtn onClick={startGame}>CELEBRATE WITH ANOTHER ROUND</GameBtn>
        </GameWon>
      )}
    </GameSection>
  );
}

export default Game;
